import { Check, ChevronDown, ChevronUp, Plus, AlertCircle, Loader } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '@/context/app-context';
import { getExerciseCategory } from '@/lib/exercise-library';
import { Logo } from '@/components/logo';

export function Workouts() {
  const { state, dispatch, sheetsStatus } = useApp();
  const navigate = useNavigate();
  const [expandedWorkout, setExpandedWorkout] = useState<string | null>(null);
  const [deleteConfirmWorkoutId, setDeleteConfirmWorkoutId] = useState<string | null>(null);

  const workoutsList = Object.values(state.workouts);

  const handleCreateWorkout = () => {
    navigate('/create-workout');
  };

  const handleEditWorkout = (workoutId: string) => {
    navigate(`/create-workout?id=${workoutId}`);
  };

  const handleStartWorkout = (workoutId: string) => {
    const workout = state.workouts[workoutId];
    if (!workout) return;
    dispatch({
      type: 'START_TRACKING',
      session: {
        workoutId: workout.id,
        exercises: workout.exercises,
        startTime: Date.now(),
      },
    });
    navigate('/track-workout');
  };

  const handleDeleteWorkout = (workoutId: string) => {
    setDeleteConfirmWorkoutId(workoutId);
  };

  const confirmDelete = (workoutId: string) => {
    dispatch({ type: 'DELETE_WORKOUT', id: workoutId });
    setDeleteConfirmWorkoutId(null);
  };

  // Get unique muscle groups for a workout
  const getMuscleGroups = (workoutId: string) => {
    const workout = state.workouts[workoutId];
    if (!workout) return [];
    const muscles = new Set<string>();
    workout.exercises.forEach((ex) => {
      const category = getExerciseCategory(ex.name, state.customExCatMap);
      muscles.add(category);
    });
    return Array.from(muscles);
  };

  // Estimate workout duration (rough: 5 min per exercise)
  const getWorkoutDuration = (workoutId: string) => {
    const workout = state.workouts[workoutId];
    if (!workout) return 0;
    return 30 + workout.exercises.length * 5;
  };

  // Sync badge logic
  const getSyncBadgeStyle = () => {
    if (!state.gapiToken) {
      return { bgColor: "#3F3F46", textColor: "#A1A1AA", icon: null, label: "Offline" };
    }
    if (sheetsStatus === 'syncing') {
      return { bgColor: "#78350F", textColor: "#FCD34D", icon: Loader, label: "Syncing..." };
    }
    if (sheetsStatus === 'error') {
      return { bgColor: "#7F1D1D", textColor: "#FCA5A5", icon: AlertCircle, label: "Sync Error" };
    }
    if (sheetsStatus === 'paused') {
      return { bgColor: "#3F3F46", textColor: "#A1A1AA", icon: null, label: "Offline" };
    }
    return { bgColor: "#1F4428", textColor: "#4ADE80", icon: Check, label: "Synced" };
  };

  const badgeStyle = getSyncBadgeStyle();
  const BadgeIcon = badgeStyle.icon;

  return (
    <div style={{ backgroundColor: '#18181B', minHeight: '100vh', paddingBottom: '32px' }}>
      <div className="p-5 pb-24">
        {/* Header */}
        <div className="flex items-center justify-between mb-5" style={{ height: '32px' }}>
          <Logo />
          {workoutsList.length > 0 && (
            <div
              className="px-2.5 py-1 rounded-full flex items-center gap-1"
              style={{ backgroundColor: badgeStyle.bgColor, color: badgeStyle.textColor, fontSize: '11px', fontWeight: 600 }}
            >
              {BadgeIcon && <BadgeIcon size={10} />}
              <span>{badgeStyle.label}</span>
            </div>
          )}
        </div>

        {/* Title */}
        <div className="flex items-center justify-between mb-5">
          <h2 style={{ color: '#F0F0F0', fontSize: '18px', fontWeight: 600 }}>Workouts</h2>
          <button
            onClick={handleCreateWorkout}
            className="flex items-center gap-1"
            style={{ color: '#FF6B35', fontSize: '14px', fontWeight: 700 }}
          >
            <Plus size={18} />
            <span>New</span>
          </button>
        </div>

        {/* Weekly / Rotating Split Toggle */}
        <div className="flex items-center gap-3 mb-6">
          <span style={{ color: '#F0F0F0', fontSize: '14px', fontWeight: 600 }}>
            Weekly / Rotating Split
          </span>
          <div
            className="flex items-center rounded-full p-1"
            style={{ backgroundColor: '#27272A' }}
          >
            <button
              className="px-3 py-1.5 rounded-full transition-colors"
              style={{ backgroundColor: '#FF6B35', color: '#18181B', fontSize: '12px', fontWeight: 600 }}
            >
              Weekly
            </button>
          </div>
        </div>

        <p style={{ color: '#71717A', fontSize: '13px', marginBottom: '24px', lineHeight: '1.6' }}>
          helps you plan your workouts for the week, without needing them to recur week to week.
        </p>

        {workoutsList.length === 0 ? (
          /* Empty State - Create New Card */
          <button
            onClick={handleCreateWorkout}
            className="w-full p-6 border-2 border-dashed flex flex-col items-center justify-center gap-2"
            style={{
              borderRadius: '16px',
              borderColor: '#3F3F46',
              backgroundColor: 'transparent',
              minHeight: '120px',
            }}
          >
            <div
              className="flex items-center justify-center rounded-full"
              style={{
                width: '48px',
                height: '48px',
                backgroundColor: '#27272A',
              }}
            >
              <Plus size={24} style={{ color: '#FF6B35' }} />
            </div>
            <span style={{ color: '#71717A', fontSize: '14px', fontWeight: 600 }}>
              Create New Workout
            </span>
          </button>
        ) : (
          /* Workout Cards */
          <div className="space-y-4">
            {workoutsList.map((workout) => {
              const muscleGroups = getMuscleGroups(workout.id);
              const duration = getWorkoutDuration(workout.id);

              return (
                <div
                  key={workout.id}
                  className="overflow-hidden"
                  style={{
                    backgroundColor: '#27272A',
                    borderRadius: '16px',
                    border: '2px solid #FF6B35',
                  }}
                >
                  <div className="p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <h3
                          style={{
                            color: '#F0F0F0',
                            fontSize: '16px',
                            fontWeight: 700,
                            marginBottom: '8px',
                          }}
                        >
                          {workout.name}
                        </h3>
                        <div className="flex flex-wrap gap-2 mb-3">
                          {muscleGroups.map((muscle, i) => (
                            <span
                              key={i}
                              className="px-2.5 py-1 rounded-full"
                              style={{
                                backgroundColor: '#3F3F46',
                                color: '#A1A1AA',
                                fontSize: '11px',
                                fontWeight: 600,
                              }}
                            >
                              {muscle}
                            </span>
                          ))}
                        </div>
                        <div className="flex items-center gap-4" style={{ color: '#71717A', fontSize: '12px' }}>
                          <span>{workout.exercises.length} exercises</span>
                          <span>~{duration} min</span>
                        </div>
                      </div>
                      <button
                        onClick={() =>
                          setExpandedWorkout(expandedWorkout === workout.id ? null : workout.id)
                        }
                        style={{
                          minWidth: '44px',
                          minHeight: '44px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                        }}
                      >
                        {expandedWorkout === workout.id ? (
                          <ChevronUp size={20} style={{ color: '#FF6B35' }} />
                        ) : (
                          <ChevronDown size={20} style={{ color: '#71717A' }} />
                        )}
                      </button>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex gap-2 mt-3">
                      <button
                        onClick={() => handleStartWorkout(workout.id)}
                        className="flex-1 px-3 py-2 rounded-lg font-semibold text-sm"
                        style={{ backgroundColor: '#FF6B35', color: '#18181B' }}
                      >
                        Start
                      </button>
                      <button
                        onClick={() => handleEditWorkout(workout.id)}
                        className="flex-1 px-3 py-2 rounded-lg font-semibold text-sm"
                        style={{ backgroundColor: '#3F3F46', color: '#F0F0F0' }}
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDeleteWorkout(workout.id)}
                        className="flex-1 px-3 py-2 rounded-lg font-semibold text-sm"
                        style={{ backgroundColor: '#3F3F46', color: '#EF4444' }}
                      >
                        Delete
                      </button>
                    </div>
                  </div>

                  {/* Expanded Exercise List */}
                  {expandedWorkout === workout.id && (
                    <div
                      className="px-4 pb-4 pt-2 border-t"
                      style={{ borderColor: '#3F3F46' }}
                    >
                      <div className="space-y-2">
                        {workout.exercises.map((exercise, i) => (
                          <div
                            key={i}
                            className="flex items-center gap-3 p-3 rounded-lg"
                            style={{ backgroundColor: '#1F2228' }}
                          >
                            <div
                              className="flex items-center justify-center rounded"
                              style={{
                                width: '24px',
                                height: '24px',
                                backgroundColor: '#3F3F46',
                                color: '#A1A1AA',
                                fontSize: '11px',
                                fontWeight: 700,
                              }}
                            >
                              {i + 1}
                            </div>
                            <div style={{ flex: 1 }}>
                              <span style={{ color: '#F0F0F0', fontSize: '13px', fontWeight: 600 }}>
                                {exercise.name}
                              </span>
                              <div style={{ color: '#71717A', fontSize: '11px', marginTop: '2px' }}>
                                {exercise.sets}x{exercise.reps}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}

            {/* Create New Card at bottom */}
            <button
              onClick={handleCreateWorkout}
              className="w-full p-6 border-2 border-dashed flex flex-col items-center justify-center gap-2"
              style={{
                borderRadius: '16px',
                borderColor: '#3F3F46',
                backgroundColor: 'transparent',
                minHeight: '120px',
              }}
            >
              <div
                className="flex items-center justify-center rounded-full"
                style={{
                  width: '48px',
                  height: '48px',
                  backgroundColor: '#27272A',
                }}
              >
                <Plus size={24} style={{ color: '#FF6B35' }} />
              </div>
              <span style={{ color: '#71717A', fontSize: '14px', fontWeight: 600 }}>
                Create New Workout
              </span>
            </button>
          </div>
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      {deleteConfirmWorkoutId && (
        <div
          className="fixed inset-0 flex items-center justify-center"
          style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)', zIndex: 50 }}
          onClick={() => setDeleteConfirmWorkoutId(null)}
        >
          <div
            className="rounded-2xl p-6 max-w-sm"
            style={{ backgroundColor: '#27272A', border: '1px solid #3F3F46' }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ color: '#F0F0F0', fontSize: '18px', fontWeight: 700, marginBottom: '8px' }}>
              Delete workout?
            </h3>
            <p style={{ color: '#71717A', fontSize: '14px', marginBottom: '20px', lineHeight: '1.6' }}>
              This will permanently remove {state.workouts[deleteConfirmWorkoutId]?.name || 'this workout'} and can't be undone.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteConfirmWorkoutId(null)}
                className="flex-1 px-4 py-2 rounded-lg font-semibold text-sm"
                style={{ backgroundColor: '#3F3F46', color: '#F0F0F0' }}
              >
                Cancel
              </button>
              <button
                onClick={() => confirmDelete(deleteConfirmWorkoutId)}
                className="flex-1 px-4 py-2 rounded-lg font-semibold text-sm"
                style={{ backgroundColor: '#EF4444', color: '#F0F0F0' }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
